/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getPortfolioById, HoldingWeightUpdate, updateHoldingWeightFromPortfolio } from '../actions/portfolio-tracker';
import { Portfolio } from '../types/portfolio';
import { AsyncThunk } from '@reducers/index';
import { handleRouteError } from '../logger';

interface SliceState {
  data?: Portfolio;
  id?: string;
  errorMessage: string;
  errored: boolean;
  loaded: boolean;
  loading: boolean;
}
const createState = (): SliceState => ({
  data: undefined,
  id: undefined,
  errorMessage: '',
  errored: false,
  loaded: false,
  loading: false,
})

export const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState: createState(),
  reducers: {
    load: (state, action: PayloadAction<string>) => ({ ...state, id: action.payload, loaded: false, loading: true }),
    reload: (state) => ({ ...state, loading: true }),
    loaded(state, action: PayloadAction<Portfolio>) {
      return { ...state, data: action.payload, loaded: true, loading: false };
    },
    handleError(state, action: PayloadAction<string>) {
      return {
        ...state,
        loaded: true,
        loading: false,
        errored: true,
        errorMessage: action.payload
      };
    },
    // @TODO should not depend on scorecard for weighting...
    // Exchange view does not even use scorecard, but we load it anyway
    updateWeighting(state, action: PayloadAction<HoldingWeightUpdate>) {
      const child = state.data?.scorecard.children.find((child) => child.initiativeId === action.payload.holdingId)
      if (child) {
        child.weight = action.payload.weight;
      }
      return state;
    }
  }
});

const { load, reload, loaded, handleError, updateWeighting } = portfolioSlice.actions

/** @deprecated should be updated to use RTK: portfolioTrackerApi */
export const loadPortfolioById = (portfolioId: string) => (dispatch: any, getState: () => any) => {
  const sliceState: SliceState = getState()[portfolioSlice.name];
  if (sliceState.loaded || sliceState.loading) {
    if (sliceState.id === portfolioId) {
      return;
    }
  }
  dispatch(load(portfolioId));

  getPortfolioById(portfolioId)
    .then((data) => {
      dispatch(loaded(data))
    })
    .catch((e) => handleError(e));
}

export const reloadPortfolioById = (portfolioId: string, flagReload: boolean = false) => (dispatch: any, getState: () => any) => {
  const sliceState: SliceState = getState()[portfolioSlice.name];
  if (sliceState.loading) {
    return;
  }

  if (flagReload) {
    dispatch(reload());
  }

  getPortfolioById(portfolioId)
    .then((data) => {
      dispatch(loaded(data))
    })
    .catch((e) => handleError(e));
}


export const updatePortfolioWeighting = (update: HoldingWeightUpdate): AsyncThunk => async (dispatch, getState) => {
  const sliceState: SliceState = getState()[portfolioSlice.name];

  const originalData = sliceState.data;
  if (!originalData) {
    handleRouteError(new Error('Portfolio not loaded'), {
      portfolioId: update.portfolioId,
      stateId: sliceState.id,
      data: typeof sliceState.data,
    });
    return
  }

  // Optimistic Update
  dispatch(updateWeighting(update));
  try {
    await updateHoldingWeightFromPortfolio(update);
  } catch (e) {
    dispatch(loaded(originalData)); // Reset data on error
    throw e;
  }

  // Reload the state to ensure scorecards are updated fully
  dispatch(reloadPortfolioById(update.portfolioId));
}

export default portfolioSlice.reducer
